import { useFlags } from 'launchdarkly-react-client-sdk';

const parseEnvId = id => (id?.includes(',') ? id.split(',') : id);

export const ReportCategories = {
  AppliedProducts: 'Applied Products',
  Fields: 'Fields',
  Fsa: 'FSA',
  Excel: 'Excel',
  Products: 'Products',
  Properties: 'Properties',
  ProductionRecords: 'Production Records',
  YieldAndProfitability: 'Yield and Profitability'
};
// These values will be displayed in the left nav
export const ReportSubTypes = {
  ByDay: 'By Day',
  ByField: 'By Field',
  CropDetails: 'By Crop Details',
  CropLayout: 'Crop Layout',
  CropZoneInfo: 'Crop Zone Info',
  Regulatory: 'Regulatory',
  Details: 'Details',
  Dicamba: 'Dicamba',
  ExcelReport: 'Excel Report',
  FertilizerByCrop: 'Fertilizer Report',
  FsaReport: 'FSA Report',
  Fsa: 'FSA',
  FsaWithMap: 'FSA With Map',
  InventorySummary: 'Inventory Summary',
  NoticeOfApplication: 'Notice of Application',
  PesticideUsage: 'Pesticide Use',
  PHI: 'PHI Report',
  PlansSummary: 'Plans Summary',
  Profitability: 'Profitability',
  ApplicatorMap: 'Applicator Map',
  PrintInvoices: 'Print Invoices',
  PrintPlans: 'Print Plans',
  PrintRecommendations: 'Print Recommendations',
  PrintTasks: 'Print Applications',
  REI: 'REI Report',
  Summary: 'Summary',
  YieldProduction: 'Yield Production'
};

export const getReport = (reports, category, subType) =>
  reports.find(
    report => report.category === category && report.subType === subType
  );

export const getReportByName = (reports, _name) =>
  reports.find(({ name }) => name === _name);

export const getReportsByCategory = (reports, _category) =>
  reports
    .filter(({ category }) => category === _category)
    .sort((a, b) => a.subType.localeCompare(b.subType));

export const reportFlagNames = {
  BY_FIELD: 'feReportOptionByField',
  APPLIED_PRODUCTS_BY_CROP_DETAILS:
    'feReportOptionAppliedProductsByCropDetails',
  APPLIED_PRODUCTS_BY_DAY: 'feReportOptionAppliedProductsByDay',
  APPLIED_PRODUCTS_DETAILS: 'feReportOptionAppliedProductsDetails',
  APPLIED_PRODUCTS_DICAMBA: 'feReportOptionAppliedProductsDicamba',
  APPLIED_PRODUCTS_FERTILIZER_BY_CROP:
    'feReportOptionAppliedProductsFertilizerByCrop',
  PESTICIDE_USAGE: 'feReportOptionPesticideUsage',
  APPLIED_PRODUCTS_PHI: 'feReportOptionAppliedProductsPhi',
  APPLIED_PRODUCTS_REI: 'feReportOptionAppliedProductsRei',
  APPLIED_PRODUCTS_SUMMARY: 'feReportOptionAppliedProductsSummary',
  CROP_LAYOUT: 'feReportOptionCropLayout',
  FSA_REPORT: 'feReportOptionFsaReport',
  FSA: 'feReportOptionFsa',
  FSA_WITH_MAP: 'feReportOptionFsaWithMap',
  INVENTORY_SUMMARY: 'feReportOptionInventorySummary',
  NOTICE_OF_APPLICATION: 'feReportOptionNoticeOfApplication',
  PLANS_SUMMARY: 'feReportOptionPlansSummary',
  APPLICATOR_MAP: 'feReportOptionApplicatorMap',
  APPLICATOR_MAP_BY_ORG: 'feReportOptionApplicatorMapByOrg',
  PRINT_INVOICES: 'feReportOptionPrintInvoices',
  PRINT_PLANS: 'feReportOptionPrintPlans',
  PRINT_RECOMMENDATIONS: 'feReportOptionPrintRecommendations',
  PRINT_TASKS: 'feReportOptionPrintTasks',
  PROPERTIES_BY_CROP_ZONE_INFO: 'feReportOptionPropertiesByCropZoneInfo',
  REGULATORY: 'feReportOptionRegulatory',
  YIELD_AND_PROFITABILITY_BY_PROFITABILITY:
    'feReportOptionYieldAndProfitabilityByProfitability',
  EXCEL_REPORT: 'feReportOptionExcelReport',
  YIELD_AND_PROFITABILITY_YIELD_PRODUCTION:
    'feReportOptionYieldAndProfitabilityYieldProduction'
};

export default () => {
  const flags = useFlags();

  const reports = [
    {
      name: 'Applied Products By Field',
      featureFlag: flags[reportFlagNames.BY_FIELD],
      category: ReportCategories.AppliedProducts,
      subType: ReportSubTypes.ByField,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: true,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "074dc791-d670-4e7c-8162-3fe6f8bdba14"
        ),
        reportId: parseEnvId(
          "db268a5d-867e-4fc0-b100-7d76f4a5556b,b4c9dd80-c35a-4bd3-ba21-2c2cd79787fb"
        )
      }
    },
    {
      name: 'Applied Products By Crop Layout',
      featureFlag: flags[reportFlagNames.APPLIED_PRODUCTS_BY_CROP_DETAILS],
      category: ReportCategories.AppliedProducts,
      subType: ReportSubTypes.CropDetails,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: true,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "30e84bd7-da03-4427-b925-41568e41af5e"
        ),
        reportId: parseEnvId(
          "b515803d-056d-47c2-986f-fd7c1b898277,fb422ca0-e4b9-4157-965a-c75e68aecad9"
        )
      }
    },
    {
      name: 'Applied Products By Day',
      featureFlag: flags[reportFlagNames.APPLIED_PRODUCTS_BY_DAY],
      category: ReportCategories.AppliedProducts,
      subType: ReportSubTypes.ByDay,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: true,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "cfa6afe9-2968-40fa-b872-5ab053d3d0f9"
        ),
        reportId: parseEnvId(
          "20feadf1-e797-42c6-9a01-cab34f2e291b,6d599db6-a89b-4355-821d-95279ecbaa11"
        )
      }
    },
    {
      name: 'Applied Products Details',
      featureFlag: flags[reportFlagNames.APPLIED_PRODUCTS_DETAILS],
      category: ReportCategories.AppliedProducts,
      subType: ReportSubTypes.Details,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: true,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "9accfbe8-b383-493d-988a-316b8c8e0f48"
        ),
        reportId: parseEnvId(
          "0e9b6888-47dc-466b-b94e-840ea4e73989,13ca93d3-b035-4b3b-9ce6-9a6d85daad7f"
        )
      }
    },
    {
      name: 'Applied Products Dicamba',
      featureFlag: flags[reportFlagNames.APPLIED_PRODUCTS_DICAMBA],
      category: ReportCategories.AppliedProducts,
      subType: ReportSubTypes.Dicamba,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: true,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "73e5cab0-c5e7-40e1-80a1-28192b8439e6"
        ),
        reportId: parseEnvId(
          "c4c95188-4ef0-429d-ba41-455deef30822,7929688e-b52c-4636-882d-e476704df76f"
        )
      }
    },
    {
      name: 'Fertilizer Report',
      featureFlag: flags[reportFlagNames.APPLIED_PRODUCTS_FERTILIZER_BY_CROP],
      category: ReportCategories.AppliedProducts,
      subType: ReportSubTypes.FertilizerByCrop,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: true,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "be8bba95-88b7-4404-8419-b9cfa38d548d"
        ),
        reportId: parseEnvId(
          "78333e90-e6fd-4116-9d60-7732c5d38c28,73f7c1ea-dce0-47d9-9f3c-c51401dfaf58"
        )
      }
    },
    {
      name: 'Pesticide Use',
      featureFlag: flags[reportFlagNames.PESTICIDE_USAGE],
      category: ReportCategories.AppliedProducts,
      subType: ReportSubTypes.PesticideUsage,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: true,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "c478bd25-04f1-49f9-91b1-e38f83a4ec73"
        ),
        reportId: parseEnvId(
          "a7ce0e2f-48c6-4582-b874-b1b84f0ef5df,3d2b6ed6-b61c-433e-9a85-df954cb6acb5"
        )
      }
    },
    {
      name: 'PHI Report',
      featureFlag: flags[reportFlagNames.APPLIED_PRODUCTS_PHI],
      category: ReportCategories.AppliedProducts,
      subType: ReportSubTypes.PHI,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: false,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "b0621b3e-fd42-4af0-9f4b-11f6a311de33"
        ),
        reportId: parseEnvId(
          "fab03ebd-7de2-40b7-89f6-cb20d862bbfe,a063b2c6-68ab-495b-ad62-72254ff3f578"
        )
      }
    },
    {
      name: 'Applied Products Regulatory',
      featureFlag: flags[reportFlagNames.REGULATORY],
      category: ReportCategories.AppliedProducts,
      subType: ReportSubTypes.Regulatory,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: true,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "4b60ac54-3c5e-4955-ad33-306aadfc3ed8"
        ),
        reportId: parseEnvId(
          "ca205366-567f-4e3e-a0b9-aab1775ccffe,30e9fe2e-79ef-4102-b58e-d76e94ce752a"
        )
      }
    },
    {
      name: 'Applied Products REI Report',
      featureFlag: flags[reportFlagNames.APPLIED_PRODUCTS_REI],
      category: ReportCategories.AppliedProducts,
      subType: ReportSubTypes.REI,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: true,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "58179a3d-155d-4b32-8ce3-08769217d401"
        ),
        reportId: parseEnvId(
          "48aeb492-c56e-495b-b00e-b3e5dd9366fe,5d24dc9d-14fa-4d88-9c16-a7b5784c17a2"
        )
      }
    },
    {
      name: 'Applied Products Summary',
      featureFlag: flags[reportFlagNames.APPLIED_PRODUCTS_SUMMARY],
      category: ReportCategories.AppliedProducts,
      subType: ReportSubTypes.Summary,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: true,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "0e68c788-75bd-486b-8be7-b6aeddd20681"
        ),
        reportId: parseEnvId(
          "03116c00-d230-4c0a-902d-dc51595e76fa,beb7f8e7-21f5-4fb1-8648-570e8877eb2b"
        )
      }
    },
    {
      name: 'Excel Report',
      featureFlag: flags[reportFlagNames.EXCEL_REPORT],
      category: ReportCategories.Excel,
      subType: ReportSubTypes.ExcelReport,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showExcelAlert: true,
        showProperties: true,
        showProducts: false,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId("82564c52-53b2-4c7e-ae34-2056d32557be"),
        reportId: parseEnvId("b1b5e76e-5aeb-46a4-971c-ed33ad638cbe,4224d39c-1032-4228-8d76-f5dd0dffea54")
      }
    },
    {
      name: 'Crop Layout',
      featureFlag: flags[reportFlagNames.CROP_LAYOUT],
      category: ReportCategories.Properties,
      subType: ReportSubTypes.CropLayout,
      formConfig: {
        showCropSeasons: true,
        showDateRange: false,
        showProperties: true,
        showProducts: false
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "20f29a73-5dca-4fa5-9032-55ecef7d2720,b5b2396b-ae01-49b4-af5b-0bc397392ceb"
        ),
        reportId: parseEnvId(
          "f446a47e-85de-4cfd-bfc3-79a7dd8906f7,217c16e4-bd2d-4f91-b668-f8d92fd2e8bb"
        )
      }
    },
    {
      name: 'FSA',
      featureFlag: flags[reportFlagNames.FSA],
      category: ReportCategories.Properties,
      subType: ReportSubTypes.Fsa,
      formConfig: {
        showCropSeasons: true,
        showDateRange: false,
        showProperties: true,
        showProducts: false
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(undefined),
        reportId: parseEnvId(
          undefined
        )
      }
    },
    {
      name: 'FSA With Map',
      featureFlag: flags[reportFlagNames.FSA_WITH_MAP],
      category: ReportCategories.Properties,
      subType: ReportSubTypes.FsaWithMap,
      formConfig: {
        showCropSeasons: true,
        showDateRange: false,
        showProperties: true,
        showProducts: false
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          undefined
        ),
        reportId: parseEnvId(
          undefined
        )
      }
    },
    {
      name: 'FSA Report',
      featureFlag: flags[reportFlagNames.FSA_REPORT],
      category: ReportCategories.FSA,
      subType: ReportSubTypes.FsaReport,
      formConfig: {
        showCropSeasons: true,
        showDateRange: false,
        showProperties: true,
        showProducts: false
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(undefined),
        reportId: parseEnvId(undefined)
      }
    },
    {
      name: 'Notice of Application',
      featureFlag: flags[reportFlagNames.NOTICE_OF_APPLICATION],
      category: ReportCategories.AppliedProducts,
      subType: ReportSubTypes.NoticeOfApplication,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: true,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "785eaeb2-6209-41eb-bbb6-3a8296eebb66"
        ),
        reportId: parseEnvId(
          "66f99aa6-19b8-46b1-8898-60cc88c323ef,9dbc82b3-3a87-47c8-affe-7766839708dd"
        )
      }
    },
    {
      name: 'Applicator Map',
      featureFlag: flags[reportFlagNames.APPLICATOR_MAP],
      featureFlagByOrg: flags[reportFlagNames.APPLICATOR_MAP_BY_ORG],
      category: ReportCategories.ProductionRecords,
      subType: ReportSubTypes.ApplicatorMap,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showWorkOrders: true,
        showProducts: false,
        showTimezoneOffset: true,
        showMapStyle: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId("3740aa69-50b8-45eb-a838-5a1aa94cef55"),
        reportId: parseEnvId(
          "60d209af-a03d-4d80-aa7c-a1259fccfd2e,f3df5fe4-971e-4ad1-b54b-1c6cf724292b"
        )
      }
    },
    {
      name: 'Print Invoices',
      featureFlag: flags[reportFlagNames.PRINT_INVOICES],
      category: ReportCategories.ProductionRecords,
      subType: ReportSubTypes.PrintInvoices,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: false,
        showProducts: false,
        showTimezoneOffset: true,
        showPrintAlert: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "babdf04e-fccb-4a96-948d-b2f98f3e33f5"
        ),
        reportId: parseEnvId(
          "0d55139a-bc33-4090-a5ec-6caf6167622c,910d5b03-9e6d-4dc8-b1a9-75f0525c247e"
        )
      }
    },
    {
      name: 'Print Plans',
      featureFlag: flags[reportFlagNames.PRINT_PLANS],
      category: ReportCategories.ProductionRecords,
      subType: ReportSubTypes.PrintPlans,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: false,
        showProducts: false,
        showTimezoneOffset: true,
        showPrintAlert: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "e3dc8556-4438-4683-8709-87ffa8dc7572"
        ),
        reportId: parseEnvId(
          "0128abaa-e9ee-4e2e-a3ba-81b8895c95e3,d9746965-b289-4f46-9caa-3f923d4b9a5c"
        )
      }
    },
    {
      name: 'Print Recommendations',
      featureFlag: flags[reportFlagNames.PRINT_RECOMMENDATIONS],
      category: ReportCategories.ProductionRecords,
      subType: ReportSubTypes.PrintRecommendations,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: false,
        showProducts: false,
        showTimezoneOffset: true,
        showPrintAlert: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "adf5b1f8-e73a-4108-810f-394a06910d93"
        ),
        reportId: parseEnvId(
          "3899fdb7-5975-4a78-9819-9a92af066294,f9787c57-ecb7-43a2-b857-04aa57529251"
        )
      }
    },

    {
      name: 'Print Applications',
      featureFlag: flags[reportFlagNames.PRINT_TASKS],
      category: ReportCategories.ProductionRecords,
      subType: ReportSubTypes.PrintTasks,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: false,
        showProducts: false,
        showTimezoneOffset: true,
        showPrintAlert: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "a9b0e341-65ff-43e7-8796-90b5c0d57a18"
        ),
        reportId: parseEnvId(
          "e58f51e2-1f8b-499a-8694-a692fff22c05,965b5d8c-92c1-465b-817e-d871a0e9fb38"
        )
      }
    },
    {
      name: 'Inventory Summary',
      featureFlag: flags[reportFlagNames.INVENTORY_SUMMARY],
      category: ReportCategories.Products,
      subType: ReportSubTypes.InventorySummary,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: false,
        showProducts: true,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "d9236def-f34c-4d10-a236-ed841cc1000e"
        ),
        reportId: parseEnvId(
          "7a76700f-2f2e-4a7d-826e-746494fdad51,778f6090-a1da-4129-81f7-e438c4b5a4c7"
        )
      }
    },
    {
      name: 'Products Plan Summary',
      featureFlag: flags[reportFlagNames.PLANS_SUMMARY],
      category: ReportCategories.Products,
      subType: ReportSubTypes.PlansSummary,
      formConfig: {
        showCropSeasons: true,
        showDateRange: false,
        showProperties: false,
        showProducts: false,
        showPlans: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "0c1300a7-ff25-40fb-bbd1-8c3ceeb1f677"
        ),
        reportId: parseEnvId(
          "40cc8458-6888-4729-9139-b099b03463f9,3f19636a-8386-4102-b17f-88bea7081ffb"
        )
      }
    },
    {
      name: 'Profitability',
      featureFlag:
        flags[reportFlagNames.YIELD_AND_PROFITABILITY_BY_PROFITABILITY],
      category: ReportCategories.YieldAndProfitability,
      subType: ReportSubTypes.Profitability,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: false,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "aefba8c4-d6f1-46cd-874f-beec2aedb266"
        ),
        reportId: parseEnvId(
          "8391c710-f300-47a5-b069-cb65ec294ee3,eae80a20-80e2-41c5-8654-747d58d8692b"
        )
      }
    },
    {
      name: 'Crop Zone Info',
      featureFlag: flags[reportFlagNames.PROPERTIES_BY_CROP_ZONE_INFO],
      category: ReportCategories.Properties,
      subType: ReportSubTypes.CropZoneInfo,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: false,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "da9d4f3a-b7ba-41b3-98fb-367a0beaa06b"
        ),
        reportId: parseEnvId(
          "b7137425-9780-4227-8ebd-c554d92b8dcc,db62dbdd-088f-4e7b-b9c5-b770646109ca"
        )
      }
    },
    {
      name: 'Yield Production',
      featureFlag:
        flags[reportFlagNames.YIELD_AND_PROFITABILITY_YIELD_PRODUCTION],
      category: ReportCategories.YieldAndProfitability,
      subType: ReportSubTypes.YieldProduction,
      formConfig: {
        showCropSeasons: true,
        showDateRange: true,
        showProperties: true,
        showProducts: false,
        showTimezoneOffset: true
      },
      reportConfig: {
        isPaginated: true,
        groupId: parseEnvId("aa609fd5-6c0e-48c7-8203-1a0f9964b4fa"),
        datasetId: parseEnvId(
          "89a2a80f-3bd7-4901-bda6-f342db4cc693"
        ),
        reportId: parseEnvId(
          "4c12edd0-d377-470b-b85d-2c3de0fe1239,4a7a5d39-8e8a-4342-9273-ca18bc9beb3c"
        )
      }
    }
  ];

  return reports;
};
